import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarExport,
    gridClasses
} from '@mui/x-data-grid';
import { GetList, GetById, AddItem, UpdateItem, DeleteItem } from '../../REST/crud';
import {
    Button,
    Dialog,
    TextField,
    Checkbox,
    FormControlLabel,
    IconButton,
    Slide,
    Typography,
    Box,
    FormHelperText,
    Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import ReactJson from 'react-json-view';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import WorkflowDialogShell from '../../themes/components/WorkflowDialogShell';
import { handleErr } from '../../helpers/errors';
import Access from '../../Utils/authorize';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function CustomToolbar() {
    return (
        <GridToolbarContainer className={gridClasses.toolbarContainer}>
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

export default function SystemSettings() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [pageSize, setPageSize] = useState(10);

    const canEdit = Access.isAdmin('Settings');
    const canDelete = Access.isAdmin('Settings');

    const toggleRefresh = () => setRefresh(r => !r);

    useEffect(() => {
        (async () => {
            const result = await GetList('SystemSetting');
            const dataArray = result.data.data || result.data || [];
            setRows(dataArray.map(e => ({ id: e._id, ...e })));
        })();
    }, [refresh]);

    const handleDelete = async (id) => {
        await DeleteItem('SystemSetting', id);
        toggleRefresh();
    };

    const handleEdit = (id) => {
        setEditId(id);
        setOpen(true);
    };

    const handleAdd = () => {
        setEditId(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        toggleRefresh();
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 0.5,
            minWidth: 150
        },
        {
            field: 'isJson',
            headerName: 'Is JSON',
            flex: 0.2,
            minWidth: 100,
            renderCell: (params) => {
                // Show Yes if isJson is true OR if jsonvalue exists
                const hasJson = params.value || (params.row.jsonvalue && Object.keys(params.row.jsonvalue).length > 0);
                return hasJson ? 'Yes' : 'No';
            }
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                if (params.row.isJson || params.row.jsonvalue) {
                    const preview = params.row.jsonvalue 
                        ? JSON.stringify(params.row.jsonvalue).substring(0, 50) + '...' 
                        : '';
                    return (
                        <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                            {preview || 'JSON Object'}
                        </Typography>
                    );
                }
                return params.value || '';
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.3,
            minWidth: 120,
            renderCell: (params) => (
                <>
                    {canEdit && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(params.id);
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    )}
                    {canDelete && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(params.id);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </>
            )
        }
    ];

    return (
        <PageShell>
            <PageHeader 
                title="System Settings"
                back
                onBack={() => navigate(-1)}
                actions={
                    canEdit && (
                        <Action
                            primary
                            icon={<AddIcon />}
                            label="Add System Setting"
                            onClick={handleAdd}
                        />
                    )
                }
            />
            <PageContent>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    autoHeight
                    components={{
                        Toolbar: CustomToolbar,
                    }}
                />
            </PageContent>
            <SystemSettingForm
                open={open}
                onClose={handleClose}
                editId={editId}
            />
        </PageShell>
    );
}

function SystemSettingForm({ open, onClose, editId }) {
    const [isJsonChecked, setIsJsonChecked] = useState(false);

    const schema = yup.object().shape({
        name: yup.string().required('Name is required'),
        isJson: yup.boolean(),
        value: yup.string().when('isJson', {
            is: false,
            then: (schema) => schema.required('Value is required when not JSON'),
            otherwise: (schema) => schema.notRequired()
        }),
        jsonvalue: yup.string().when('isJson', {
            is: true,
            then: (schema) => schema.required('JSON Value is required when isJson is checked')
                .test('valid-json', 'Must be valid JSON', function(value) {
                    if (!value) return false;
                    try {
                        JSON.parse(value);
                        return true;
                    } catch (e) {
                        return false;
                    }
                }),
            otherwise: (schema) => schema.notRequired()
        })
    });

    const { control, handleSubmit, setError, formState: { errors }, reset, watch } = useForm({
        defaultValues: {
            name: '',
            isJson: false,
            value: '',
            jsonvalue: ''
        },
        resolver: yupResolver(schema)
    });

    const watchIsJson = watch('isJson');

    useEffect(() => {
        setIsJsonChecked(watchIsJson);
    }, [watchIsJson]);

    useEffect(() => {
        (async () => {
            if (editId) {
                const resp = await GetById('SystemSetting', editId);
                const data = resp.data;
                // Auto-detect: if jsonvalue exists, treat as JSON
                const hasJsonValue = data.jsonvalue && Object.keys(data.jsonvalue).length > 0;
                reset({
                    name: data.name || '',
                    isJson: hasJsonValue ? true : (data.isJson || false),
                    value: data.value || '',
                    jsonvalue: hasJsonValue ? JSON.stringify(data.jsonvalue, null, 2) : ''
                });
            } else {
                reset({
                    name: '',
                    isJson: false,
                    value: '',
                    jsonvalue: ''
                });
            }
        })();
    }, [editId, reset]);

    const onSubmit = async (data) => {
        try {
            const payload = {
                name: data.name,
                isJson: data.isJson,
                value: data.isJson ? '' : data.value,
                jsonvalue: data.isJson ? JSON.parse(data.jsonvalue) : null
            };

            if (editId) {
                await UpdateItem('SystemSetting', editId, payload);
            } else {
                await AddItem('SystemSetting', payload);
            }
            onClose();
        } catch (err) {
            handleErr(err, setError);
        }
    };

    return (
        <WorkflowDialogShell
            open={open}
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
            title={editId ? 'Edit System Setting' : 'Add System Setting'}
            transitionComponent={Transition}
        >
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-12'>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    variant='standard'
                                    margin='dense'
                                    fullWidth
                                    label="Name"
                                    required
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )}
                        />

                        <Controller
                            name="isJson"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                    }
                                    label="Is JSON Value"
                                />
                            )}
                        />

                        {!isJsonChecked && (
                            <Controller
                                name="value"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        variant='standard'
                                        margin='dense'
                                        fullWidth
                                        label="Value (Text)"
                                        required
                                        error={!!errors.value}
                                        helperText={errors.value?.message}
                                    />
                                )}
                            />
                        )}

                        {isJsonChecked && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    JSON Value * (Visual Editor)
                                </Typography>
                                <Controller
                                    name="jsonvalue"
                                    control={control}
                                    render={({ field }) => {
                                        let jsonObj = {};
                                        try {
                                            jsonObj = field.value ? JSON.parse(field.value) : {};
                                        } catch (e) {
                                            jsonObj = { error: 'Invalid JSON' };
                                        }
                                        
                                        return (
                                            <Paper elevation={2} sx={{ p: 2, maxHeight: '500px', overflow: 'auto' }}>
                                                <ReactJson
                                                    src={jsonObj}
                                                    theme="rjv-default"
                                                    iconStyle="triangle"
                                                    collapsed={false}
                                                    displayDataTypes={false}
                                                    displayObjectSize={true}
                                                    enableClipboard={true}
                                                    onEdit={(edit) => {
                                                        field.onChange(JSON.stringify(edit.updated_src, null, 2));
                                                        return true;
                                                    }}
                                                    onAdd={(add) => {
                                                        field.onChange(JSON.stringify(add.updated_src, null, 2));
                                                        return true;
                                                    }}
                                                    onDelete={(del) => {
                                                        field.onChange(JSON.stringify(del.updated_src, null, 2));
                                                        return true;
                                                    }}
                                                />
                                            </Paper>
                                        );
                                    }}
                                />
                                {errors.jsonvalue && (
                                    <FormHelperText error sx={{ mt: 1 }}>
                                        {errors.jsonvalue?.message}
                                    </FormHelperText>
                                )}
                                <FormHelperText sx={{ mt: 1 }}>
                                    💡 Click on values to edit, use + to add fields, hover over keys to see delete option
                                </FormHelperText>
                            </Box>
                        )}
                    </div>
                </div>
            </div>
        </WorkflowDialogShell>
    );
}
